import { GraphQLResult, graphqlOperation } from "@aws-amplify/api-graphql";
import { generateClient } from "aws-amplify/api";
import { OpenLevelNameEnum } from "common/enums/OpenLevelEnum";
import { CheckStickyNotesCondition } from "common/interfaces/CheckStickyNotesCondition";
import { StickyNoteSearchConditions } from "common/interfaces/StickyNoteSearchConditions";
import {
  Company,
  CreateStickyNoteFMutation,
  CreateStickyNoteFMutationVariables,
  DeleteStickyNoteFMutation,
  DeleteStickyNoteFMutationVariables,
  GetStickyNoteQuery,
  GetStickyNoteQueryVariables,
  ListStickyNotesBySearchInfoQuery,
  ListStickyNotesBySearchInfoQueryVariables,
  StickyNote,
  UpdateStickyNoteFMutation,
  UpdateStickyNoteFMutationVariables,
  User
} from "graphql/API";
import {
  createStickyNoteF as createStickyNoteFMutation,
  deleteStickyNoteF as deleteStickyNoteFMutation,
  updateStickyNoteF as updateStickyNoteFMutation
} from "graphql/mutations";
import {
  getStickyNote as getStickyNoteQuery,
  listStickyNotesBySearchInfo
} from "graphql/queries";
import { getSumoraAuthToken } from "../credentials";
import { getCompaniesFromRecNo } from "./companies";
import { listUsersByCompanyRecNo } from "./users";

// メモ検索(一覧用)
export const listStickyNotes = async (
  conditions: CheckStickyNotesCondition
) => {
  const stickyNotes: Map<
    string,
    StickyNote & {
      ownerName: string;
      branchName: string;
    }
  > = new Map();

  const users = await listUsersByCompanyRecNo(
    String(conditions.company?.recNo || "")
  );

  const usersMap: Map<string, User> = new Map();

  users.forEach((user) => {
    usersMap.set(user.username, user);
  });

  const companies = await getCompaniesFromRecNo(conditions.company?.recNo || 0);

  const companiesMap: Map<string, Company> = new Map();

  companies.forEach((company) => {
    companiesMap.set(company.id, company);
  });

  const variablesList: ListStickyNotesBySearchInfoQueryVariables[] = [];

  for (const openLevel of conditions.openLevels) {
    const variables: ListStickyNotesBySearchInfoQueryVariables = {
      infoName: "companyRecNoAndTileOnZoom12",
      searchInfo: { beginsWith: `${conditions.company?.recNo}#` },
      limit: 100
    };

    if (openLevel === OpenLevelNameEnum.支店内) {
      variables.filter = {
        companyId: {
          eq: conditions.company?.id
        }
      };
    }

    variablesList.push(variables);
  }

  const openLevel = conditions.openLevels.every(
    (x) => x === OpenLevelNameEnum.社内
  )
    ? "companyOnly"
    : conditions.openLevels.every((x) => x === OpenLevelNameEnum.支店内)
      ? "branchOnly"
      : "both";

  const responses = variablesList.map(async (variables) => {
    let nextToken: string | null = "start";
    const client = generateClient();

    while (nextToken) {
      if (nextToken !== "start") {
        variables.nextToken = nextToken;
      }

      let items: (StickyNote | null)[] = [];

      const res = (await client.graphql({
        ...graphqlOperation(listStickyNotesBySearchInfo, variables),
        authMode: "lambda",
        authToken: await getSumoraAuthToken()
      })) as GraphQLResult<ListStickyNotesBySearchInfoQuery>;

      if (res.data?.listStickyNotesBySearchInfo?.items) {
        items = res.data?.listStickyNotesBySearchInfo?.items.map((x) => ({
          ...x,
          __typename: "StickyNote"
        })) as StickyNote[];
      }

      nextToken = res.data?.listStickyNotesBySearchInfo?.nextToken || null;

      // 返却値のセット
      for (const item of items) {
        if (item) {
          if (
            (openLevel === "companyOnly" &&
              (item.openLevel === OpenLevelNameEnum.社内 ||
                item.openLevel === null ||
                item.openLevel === undefined)) ||
            (openLevel === "branchOnly" &&
              item.openLevel === OpenLevelNameEnum.支店内) ||
            openLevel === "both"
          ) {
            const ownerName = usersMap.has(item.owner)
              ? (usersMap.get(item.owner)?.name ?? "？")
              : "？";

            const branchName = companiesMap.has(item.companyId)
              ? (companiesMap.get(item.companyId)?.branchName ?? "？")
              : "-";

            stickyNotes.set(item.id, { ...item, ownerName, branchName });
          }
        }
      }
    }
  });

  await Promise.all(responses);

  return Array.from(stickyNotes.values());
};

// メモ検索
export const searchStickyNote = async (
  conditions: StickyNoteSearchConditions,
  stickyNotesCountSetter?: (value: number | null) => void
) => {
  const stickyNotes: StickyNote[] = [];

  const variablesList = constructVariablesForStickyNote(conditions);

  const responses = variablesList.map(async (variables) => {
    let nextToken: string | null = "start";
    const client = generateClient();

    while (nextToken) {
      if (nextToken !== "start") {
        variables.nextToken = nextToken;
      }

      let items: (StickyNote | null)[] = [];

      const res = (await client.graphql({
        ...graphqlOperation(listStickyNotesBySearchInfo, variables),
        authMode: "lambda",
        authToken: await getSumoraAuthToken()
      })) as GraphQLResult<ListStickyNotesBySearchInfoQuery>;

      if (res.data?.listStickyNotesBySearchInfo?.items) {
        items = res.data?.listStickyNotesBySearchInfo?.items.map((x) => ({
          ...x,
          __typename: "StickyNote"
        })) as StickyNote[];
      }

      nextToken = res.data?.listStickyNotesBySearchInfo?.nextToken || null;

      // 返却値のセット
      items.forEach((x) => {
        if (x) {
          stickyNotes.push(x);
        }
      });

      if (stickyNotesCountSetter) {
        stickyNotesCountSetter(stickyNotes.length);
      }
    }
  });

  await Promise.all(responses);

  return stickyNotes;
};

// パラメータ構築
const constructVariablesForStickyNote = (
  conditions: StickyNoteSearchConditions
): ListStickyNotesBySearchInfoQueryVariables[] => {
  const innerConditions = { ...conditions };

  const variablesList: ListStickyNotesBySearchInfoQueryVariables[] = [];

  const tileOnZoom12Exists = innerConditions.tilesOnZoom12.length > 0;
  const tileOnZoom14Exists = innerConditions.tilesOnZoom14.length > 0;
  const tileOnZoom16Exists = innerConditions.tilesOnZoom16.length > 0;

  // Queryパラメータ構築
  for (const companyRecNo of [
    "-",
    ...innerConditions.companies.map((x) => x.recNo)
  ]) {
    // 位置範囲検索
    for (const tile of tileOnZoom12Exists
      ? innerConditions.tilesOnZoom12
      : tileOnZoom14Exists
        ? innerConditions.tilesOnZoom14
        : tileOnZoom16Exists
          ? innerConditions.tilesOnZoom16
          : []) {
      const variables: ListStickyNotesBySearchInfoQueryVariables = {
        infoName: `companyRecNoAndTileOnZoom${
          tileOnZoom12Exists
            ? "12"
            : tileOnZoom14Exists
              ? "14"
              : tileOnZoom16Exists
                ? "16"
                : ""
        }`,
        searchInfo: { eq: `${companyRecNo}#${tile}` },
        limit: 100
      };

      variablesList.push(variables);
    }
  }

  return variablesList;
};

// メモ取得
export const getStickyNote = async (id: string, infoName: string) => {
  const client = generateClient();
  const res = (await client.graphql({
    ...graphqlOperation(getStickyNoteQuery, {
      id: id,
      infoName: infoName
    } as GetStickyNoteQueryVariables),
    authMode: "lambda",
    authToken: await getSumoraAuthToken()
  })) as GraphQLResult<GetStickyNoteQuery>;

  return res.data?.getStickyNote;
};

export const createStickyNote = async (stickyNote: StickyNote) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename: _, ...input } = { ...stickyNote };
  const variables: CreateStickyNoteFMutationVariables = { input: input };
  const client = generateClient();

  const res = (await client.graphql({
    ...graphqlOperation(createStickyNoteFMutation, variables),
    authMode: "lambda",
    authToken: await getSumoraAuthToken()
  })) as GraphQLResult<CreateStickyNoteFMutation>;

  if (res.errors) {
    throw res.errors;
  }

  if (res.data?.createStickyNoteF?.errorMessage) {
    throw res.data.createStickyNoteF.errorMessage;
  }

  return res.data?.createStickyNoteF;
};

export const updateStickyNote = async (stickyNote: StickyNote) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename: _, ...input } = { ...stickyNote };
  const variables: UpdateStickyNoteFMutationVariables = { input: input };
  const client = generateClient();

  const res = (await client.graphql({
    ...graphqlOperation(updateStickyNoteFMutation, variables),
    authMode: "lambda",
    authToken: await getSumoraAuthToken()
  })) as GraphQLResult<UpdateStickyNoteFMutation>;

  if (res.errors) {
    throw res.errors;
  }

  if (res.data?.updateStickyNoteF?.errorMessage) {
    throw res.data.updateStickyNoteF.errorMessage;
  }

  return res.data?.updateStickyNoteF;
};

export const deleteStickyNote = async (stickyNote: StickyNote) => {
  const variables: DeleteStickyNoteFMutationVariables = {
    input: {
      id: stickyNote.id,
      infoName: stickyNote.infoName,
      companyId: stickyNote.companyId
    }
  };
  const client = generateClient();

  const res = (await client.graphql({
    ...graphqlOperation(deleteStickyNoteFMutation, variables),
    authMode: "lambda",
    authToken: await getSumoraAuthToken()
  })) as GraphQLResult<DeleteStickyNoteFMutation>;

  if (res.errors) {
    throw res.errors;
  }

  if (res.data?.deleteStickyNoteF?.errorMessage) {
    throw res.data.deleteStickyNoteF.errorMessage;
  }

  return res.data?.deleteStickyNoteF;
};
